/**
 * @file Camp Database Service
 * @description Service for saving and managing camp data in camp_audit_data table
 * 
 * KEY FEATURES:
 * - Saves complete Google Places Maximum tier data
 * - Preserves engagement metrics on updates (view/save/click counts)
 * - Preserves community contributions (doesn't overwrite with Google data)
 * - Handles both INSERT (new camps) and UPDATE (existing camps)
 * - Fixes Gap 1: Category field mapping bug (supports both nested and flat structures)
 * - Fixes Gap 2: Saves phone/website/contact information
 * - Tracks field tier level (minimal/search/essential/enhanced/maximum)
 * - Calculates popularity scores from multiple signals
 * 
 * FIELD TIERS SUPPORTED:
 * - Basic Data: Core identity, location, categories, accessibility
 * - Contact Data: Phone (formatted + international), website, structured address
 * - Atmosphere Data: Hours, ratings, reviews, photos, price level
 * 
 * IMPORTANT NOTES:
 * - Community data (user contributions) is NEVER overwritten by Google data
 * - Engagement metrics are ALWAYS preserved on updates
 * - Popularity score is recalculated using both Google and engagement data
 * - The function uses maybeSingle() to avoid PGRST116 errors
 * 
 * @see transformGooglePlaceForDB - Transforms Google data before saving
 * @see trackCampEngagement - Tracks user engagement (views/saves/clicks)
 */

// Import distance calculation utility
import { calculateDistance } from '@/utils/distanceCalculation';

// Lazy load supabase for Node.js compatibility (avoid top-level import of @/lib path alias)
let supabaseClient = null;
let supabaseModule = null;

async function getSupabaseClient() {
  if (supabaseClient) return supabaseClient;
  
  try {
    // Try to import from Vite path alias (browser/Vite environment)
    if (!supabaseModule) {
      supabaseModule = await import('@/lib/supabase');
    }
    supabaseClient = supabaseModule.supabase;
    return supabaseClient;
  } catch (error) {
    // Node.js environment - path alias doesn't work, client must be provided as parameter
    return null;
  }
}

/**
 * Search camps in database first (cache check)
 * @param {Object} searchParams - Location, radius, categories, etc.
 * @param {Object} supabaseClient - Optional Supabase client (for Node.js scripts)
 * @returns {Array} Cached camp results
 */
export const searchCampsInDatabase = async (searchParams, supabaseClient = null) => {
  const { location, radius, categories, query } = searchParams;
  const client = supabaseClient || await getSupabaseClient();
  
  if (!client) {
    throw new Error('Supabase client not available. Provide supabaseClient parameter in Node.js environment.');
  }
  
  try {
    console.log('🔍 Searching database cache first...');
    
    // Parse query for camp_type or activities filtering
    let campTypeFilter = null;
    let activitySearch = null;
    let useTextSearch = true; // Default to text search unless we have specific filters
    
    if (query) {
      const normalizedQuery = query.toLowerCase().trim();
      
      // Check for camp_type matches
      // Priority 1: Check for "overnight" (matches "overnight", "overnight camp", etc.)
      if (normalizedQuery.includes('overnight')) {
        campTypeFilter = 'Overnight Camp';
        useTextSearch = false; // Don't use text search when we have specific camp_type filter
        console.log('🏕️ Detected camp_type filter: Overnight Camp');
      } 
      // Priority 2: Check for "day camp" or exact "day" (but not "overnight")
      else if (!normalizedQuery.includes('overnight') && 
               (normalizedQuery === 'day' || normalizedQuery.includes('day camp'))) {
        campTypeFilter = 'Day Camp';
        useTextSearch = true; // Still use text search for name matching
        console.log('☀️ Detected camp_type filter: Day Camp');
      } 
      // Priority 3: For business/camp name searches, use text search (which searches name via search_vector)
      // Only use activities search for very short queries that look like activity keywords
      else {
        // If query looks like a business/camp name (has multiple words, capital letters, or specific patterns),
        // use text search instead of activities search
        const looksLikeName = normalizedQuery.split(/\s+/).length >= 2 || 
                              /[A-Z]/.test(query) || // Has capital letters
                              normalizedQuery.includes('center') ||
                              normalizedQuery.includes('camp') ||
                              normalizedQuery.includes('community') ||
                              normalizedQuery.includes('ymca') ||
                              normalizedQuery.includes('jcc');
        
        if (looksLikeName) {
          useTextSearch = true; // Use text search for name matching
          console.log('🏢 Detected business/camp name search, using text search');
        } else {
          activitySearch = normalizedQuery;
          useTextSearch = false; // Use activities search for short activity keywords
          console.log('🎨 Detected activities search:', activitySearch);
        }
      }
    }
    
    // select('*') so transformDatabaseCamp receives community/contribution fields (has_community_enhancements, contact_phone, etc.)
    let dbQuery = client
      .from('camp_audit_data')
      .select('*');
    
    // Detect if query looks like a business/camp name (skip location filtering for these)
    const isBusinessNameQuery = query && (
      query.split(/\s+/).length >= 2 ||
      query.toLowerCase().includes('center') ||
      query.toLowerCase().includes('camp') ||
      query.toLowerCase().includes('community') ||
      query.toLowerCase().includes('ymca') ||
      query.toLowerCase().includes('jcc') ||
      query.toLowerCase().includes('jewish') ||
      query.toLowerCase().includes('academy') ||
      query.toLowerCase().includes('school')
    );
    
    // Location-based search (within radius) - skip for business name queries
    // Business name searches should search everywhere, not just nearby
    if (location && radius && !isBusinessNameQuery) {
      const radiusInDegrees = radius / 69; // Rough conversion: 1 degree ≈ 69 miles
      
      dbQuery = dbQuery
        .gte('coordinates_lat', location.lat - radiusInDegrees)
        .lte('coordinates_lat', location.lat + radiusInDegrees)
        .gte('coordinates_lng', location.lng - radiusInDegrees)
        .lte('coordinates_lng', location.lng + radiusInDegrees);
      
      console.log('📍 Applying location filter:', { lat: location.lat, lng: location.lng, radius: radius + ' miles' });
    } else if (location && radius && isBusinessNameQuery) {
      console.log('🏢 Business name query detected - skipping location filter to find exact match');
    }
    
    // Category filtering
    if (categories && categories.length > 0) {
      dbQuery = dbQuery.overlaps('all_categories', categories);
    }
    
    // Camp type filtering (prioritized over activities)
    if (campTypeFilter) {
      dbQuery = dbQuery.eq('camp_type', campTypeFilter);
    }
    
    // Activities filtering (only if no camp_type match)
    if (activitySearch && !campTypeFilter) {
      dbQuery = dbQuery.ilike('activities', `%${activitySearch}%`);
    }
    
    // Text search (only if no specific filters applied)
    if (query && useTextSearch) {
      // Use textSearch on search_vector for full-text search
      // This searches across name, location, and other indexed fields
      dbQuery = dbQuery.textSearch('search_vector', query, {
        type: 'plain',      // ✅ Handles "Staenberg-Loup Jewish Community Center" naturally
        config: 'english'
      });
    }
    
    // REMOVED: Quality filters - show all camps from database regardless of freshness or refresh status
    // Previously filtered by:
    // - data_freshness_days <= 30 (removed - show all camps regardless of age)
    // - needs_refresh (removed - show all camps even if marked for refresh)
    // This ensures that any camp stored in the database will display in search results
    
    // Order by popularity for initial fetch (we'll re-sort by distance if location provided)
    dbQuery = dbQuery.order('popularity_score', { ascending: false });
    
    // Limit results to prevent overwhelming responses
    dbQuery = dbQuery.limit(50);
    
    const { data, error } = await dbQuery;
    
    if (error) throw error;
    
    console.log(`✅ Found ${data?.length || 0} camps in database cache`);
    
    // If location is provided, calculate distance and sort by distance first
    if (location && data && data.length > 0) {
      // Calculate distance for each camp
      const campsWithDistance = data.map(camp => {
        const campLat = parseFloat(camp.coordinates_lat);
        const campLng = parseFloat(camp.coordinates_lng);
        
        // Only calculate distance if coordinates are valid
        if (!isNaN(campLat) && !isNaN(campLng) && campLat !== 0 && campLng !== 0) {
          const distance = calculateDistance(
            location.lat,
            location.lng,
            campLat,
            campLng
          );
          return {
            ...camp,
            distance_miles: distance
          };
        }
        
        // If coordinates are invalid, set a large distance so they appear last
        return {
          ...camp,
          distance_miles: 999999
        };
      });
      
      // If we have a query, prioritize exact name matches first, then sort by distance
      if (query && campsWithDistance.length > 0) {
        const queryLower = query.toLowerCase().trim();
        
        // Find exact matches (case-insensitive)
        const exactMatches = campsWithDistance.filter(camp => {
          const nameLower = (camp.name || '').toLowerCase().trim();
          return nameLower === queryLower || nameLower.includes(queryLower) || queryLower.includes(nameLower);
        });
        
        // Find close matches (fuzzy matching)
        const closeMatches = campsWithDistance.filter(camp => {
          if (exactMatches.includes(camp)) return false;
          const nameLower = (camp.name || '').toLowerCase().trim();
          const queryWords = queryLower.split(/\s+/).filter(w => w.length > 2);
          return queryWords.length > 0 && queryWords.every(word => nameLower.includes(word));
        });
        
        // Other results
        const otherResults = campsWithDistance.filter(camp => 
          !exactMatches.includes(camp) && !closeMatches.includes(camp)
        );
        
        // Sort each group by distance, then combine
        const sortByDistance = (arr) => arr.sort((a, b) => {
          if (a.distance_miles !== b.distance_miles) {
            return a.distance_miles - b.distance_miles;
          }
          const popularityA = a.popularity_score || 0;
          const popularityB = b.popularity_score || 0;
          return popularityB - popularityA;
        });
        
        return [...sortByDistance(exactMatches), ...sortByDistance(closeMatches), ...sortByDistance(otherResults)];
      }
      
      // Sort by distance first (closest first), then by popularity
      campsWithDistance.sort((a, b) => {
        // Primary sort: distance (ascending)
        if (a.distance_miles !== b.distance_miles) {
          return a.distance_miles - b.distance_miles;
        }
        // Secondary sort: popularity (descending)
        const popularityA = a.popularity_score || 0;
        const popularityB = b.popularity_score || 0;
        return popularityB - popularityA;
      });
      
      return campsWithDistance;
    }
    
    // No location provided, prioritize exact matches, then return results sorted by popularity
    if (query && data && data.length > 0) {
      const queryLower = query.toLowerCase().trim();
      
      // Find exact matches (case-insensitive)
      const exactMatches = data.filter(camp => {
        const nameLower = (camp.name || '').toLowerCase().trim();
        return nameLower === queryLower || nameLower.includes(queryLower) || queryLower.includes(nameLower);
      });
      
      // Find close matches (fuzzy matching)
      const closeMatches = data.filter(camp => {
        if (exactMatches.includes(camp)) return false;
        const nameLower = (camp.name || '').toLowerCase().trim();
        const queryWords = queryLower.split(/\s+/).filter(w => w.length > 2);
        return queryWords.length > 0 && queryWords.every(word => nameLower.includes(word));
      });
      
      // Other results
      const otherResults = data.filter(camp => 
        !exactMatches.includes(camp) && !closeMatches.includes(camp)
      );
      
      // Sort each group by popularity, then combine
      const sortByPopularity = (arr) => arr.sort((a, b) => {
        const popularityA = a.popularity_score || 0;
        const popularityB = b.popularity_score || 0;
        return popularityB - popularityA;
      });
      
      return [...sortByPopularity(exactMatches), ...sortByPopularity(closeMatches), ...sortByPopularity(otherResults)];
    }
    
    return data || [];
    
  } catch (error) {
    console.error('❌ Database search failed:', error);
    return [];
  }
};

/**
 * Save or update a camp in the camp_audit_data table
 * Handles complete Maximum tier Google Places data
 * 
 * @param {Object} campData - Transformed camp data from transformGooglePlaceForDB
 * @param {Object} supabaseClientParam - Optional Supabase client (for Node.js scripts)
 * @returns {Promise<Object>} Result with success status and metadata
 */
export const saveCampToDatabase = async (campData, supabaseClientParam = null) => {
  try {
    // Get Supabase client - use provided one or try to get from import
    const client = supabaseClientParam || await getSupabaseClient();
    
    if (!client) {
      throw new Error('Supabase client not available. Provide supabaseClientParam in Node.js environment.');
    }
    
    // =========================================================================
    // CHECK IF CAMP EXISTS
    // =========================================================================
    
    // Query by place_id to find existing camp
    const { data: existing, error: queryError } = await client
      .from('camp_audit_data')
      .select('id, place_id, view_count, save_count, click_count, community_contribution_count, data_confidence_score')
      .eq('place_id', campData.place_id)
      .maybeSingle();  // Use maybeSingle to avoid PGRST116 error

    if (queryError) {
      console.error('Error checking for existing camp:', queryError);
      throw queryError;
    }

    // =========================================================================
    // BUILD CAMP RECORD WITH ALL MAXIMUM TIER FIELDS
    // =========================================================================
    
    // Handle coordinates - support both location object and direct lat/lng
    const lat = campData.coordinates_lat || campData.location?.lat || null;
    const lng = campData.coordinates_lng || campData.location?.lng || null;

    const campRecord = {
      // -----------------------------------------------------------------------
      // Core Identity (Basic Data)
      // -----------------------------------------------------------------------
      place_id: campData.place_id,
      name: campData.name,
      location: campData.location || campData.formatted_address || '',
      formatted_address: campData.formatted_address || null,
      
      // -----------------------------------------------------------------------
      // Coordinates (Basic Data)
      // -----------------------------------------------------------------------
      coordinates_lat: lat ? Number(lat) : null,
      coordinates_lng: lng ? Number(lng) : null,
      
      // -----------------------------------------------------------------------
      // Contact Information (Contact Data tier) - FIX FOR GAP 2
      // -----------------------------------------------------------------------
      phone: campData.phone || null,
      international_phone_number: campData.international_phone_number || null,
      website: campData.website || null,
      contact_email: campData.contact_email || null,
      adr_address: campData.adr_address || null,
      
      // -----------------------------------------------------------------------
      // Location Enhancement (Basic Data) - NEW MAXIMUM TIER FIELDS
      // -----------------------------------------------------------------------
      google_maps_url: campData.google_maps_url || null,
      vicinity: campData.vicinity || null,
      icon_url: campData.icon_url || null,
      plus_code_global: campData.plus_code_global || null,
      plus_code_local: campData.plus_code_local || null,
      utc_offset_minutes: campData.utc_offset_minutes || null,
      
      // -----------------------------------------------------------------------
      // Accessibility & Attributes (Basic Data) - NEW MAXIMUM TIER FIELDS
      // -----------------------------------------------------------------------
      wheelchair_accessible: campData.wheelchair_accessible || null,
      
      // -----------------------------------------------------------------------
      // Operating Hours (Atmosphere Data tier) - NEW MAXIMUM TIER FIELDS
      // -----------------------------------------------------------------------
      opening_hours: campData.opening_hours || null,
      current_opening_hours: campData.current_opening_hours || null,
      
      // -----------------------------------------------------------------------
      // Ratings & Business Info (Atmosphere Data tier)
      // -----------------------------------------------------------------------
      rating: campData.rating || null,
      user_ratings_total: campData.user_ratings_total || null,
      price_level: campData.price_level || null,
      business_status: campData.business_status || null,
      
      // -----------------------------------------------------------------------
      // Media (Atmosphere Data tier)
      // -----------------------------------------------------------------------
      photos: campData.photos || null,
      photo_count: campData.photo_count || 0,
      
      // -----------------------------------------------------------------------
      // Reviews (Atmosphere Data tier)
      // -----------------------------------------------------------------------
      reviews: campData.reviews || null,
      editorial_summary: campData.editorial_summary || null,
      
      // Note: We store Google's editorial in editorial_summary
      // review_summary is a separate field for aggregated community reviews (future)
      
      // -----------------------------------------------------------------------
      // Google Types (Basic Data)
      // -----------------------------------------------------------------------
      google_types: campData.google_types || [],
      
      // -----------------------------------------------------------------------
      // Categorization (Basic Data) - FIX FOR GAP 1
      // Support both flat (primary_category) and nested (program_category.primary_category)
      // -----------------------------------------------------------------------
      camp_type: campData.camp_type || campData.primary_category || campData.program_category?.primary_category || null,
      primary_category: campData.primary_category || campData.program_category?.primary_category || null,
      all_categories: campData.all_categories || campData.program_category?.all_categories || [],
      category_confidence: campData.category_confidence || campData.confidence || campData.program_category?.confidence || null,
      keywords_matched: campData.keywords_matched || campData.program_category?.keywords_matched || [],
      
      // -----------------------------------------------------------------------
      // Search Optimization
      // -----------------------------------------------------------------------
      search_keywords: extractSearchKeywords(campData),
      popularity_score: campData.popularity_score || 0,
      
      // -----------------------------------------------------------------------
      // Cache & Freshness Metadata - NEW MAXIMUM TIER TRACKING
      // -----------------------------------------------------------------------
      last_fetched_from_google: campData.last_fetched_from_google || new Date().toISOString(),
      last_details_fetch: campData.last_details_fetch || new Date().toISOString(),
      details_fetch_tier: campData.details_fetch_tier || 'maximum',
      needs_refresh: false,
      
      // Calculate data freshness in days
      data_freshness_days: 0,  // Just fetched
      
      // -----------------------------------------------------------------------
      // Audit Fields
      // -----------------------------------------------------------------------
      audit_date: campData.audit_date || new Date().toISOString(),
      audit_year: campData.audit_year || new Date().getFullYear(),
      audit_week: campData.audit_week || getWeekNumber(new Date()),
      api_compliant: campData.api_compliant !== false,  // Default true
      ethical_source: campData.ethical_source !== false,  // Default true
      
      // -----------------------------------------------------------------------
      // Source Tracking
      // -----------------------------------------------------------------------
      source: campData.source || 'google_places_api'
    };

    // =========================================================================
    // UPDATE EXISTING CAMP OR INSERT NEW CAMP
    // =========================================================================
    
    if (existing) {
      // ----------------------------------------------------------------------
      // UPDATE: Camp already exists, preserve engagement metrics
      // ----------------------------------------------------------------------
      
      const updateData = {
        ...campRecord,
        // CRITICAL: Preserve existing engagement and community data
        view_count: existing.view_count,
        save_count: existing.save_count,
        click_count: existing.click_count,
        // Don't overwrite community contributions from Google data
        community_contribution_count: existing.community_contribution_count,
        data_confidence_score: existing.data_confidence_score,
        // Recalculate popularity score based on updated data + preserved engagement
        popularity_score: calculatePopularityScore({
          rating: campRecord.rating,
          user_ratings_total: campRecord.user_ratings_total,
          view_count: existing.view_count,
          save_count: existing.save_count,
          click_count: existing.click_count,
          photo_count: campRecord.photo_count,
          has_website: !!campRecord.website,
          has_phone: !!campRecord.phone,
          business_status: campRecord.business_status
        })
      };

      // Don't update these system fields on refresh
      delete updateData.id;
      delete updateData.created_at;
      delete updateData.audit_id;

      const { error: updateError } = await client
        .from('camp_audit_data')
        .update(updateData)
        .eq('place_id', campData.place_id);

      if (updateError) {
        console.error('Error updating camp:', updateError);
        throw updateError;
      }

      console.log(`✅ Updated camp: ${campData.name} (${campData.place_id})`);
      
      return { 
        success: true, 
        action: 'updated', 
        place_id: campData.place_id,
        camp_id: existing.id,
        name: campData.name
      };

    } else {
      // ----------------------------------------------------------------------
      // INSERT: New camp, initialize engagement metrics
      // ----------------------------------------------------------------------
      
      const insertData = {
        ...campRecord,
        // Initialize engagement metrics for new camps
        view_count: 0,
        save_count: 0,
        click_count: 0,
        // Initialize community fields (will be updated if/when users contribute)
        community_contribution_count: 0,
        data_confidence_score: 50,  // Neutral starting confidence
        has_community_enhancements: false,
        user_contributed: false,
        // Generate unique audit_id if not provided
        audit_id: campData.audit_id || `CAMP-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      };

      const { data: inserted, error: insertError } = await client
        .from('camp_audit_data')
        .insert(insertData)
        .select('id')
        .single();

      if (insertError) {
        console.error('Error inserting camp:', insertError);
        throw insertError;
      }

      console.log(`✅ Inserted new camp: ${campData.name} (${campData.place_id})`);

      return { 
        success: true, 
        action: 'inserted', 
        place_id: campData.place_id,
        camp_id: inserted.id,
        name: campData.name
      };
    }

  } catch (error) {
    console.error('Error in saveCampToDatabase:', error);
    return {
      success: false,
      error: error.message,
      place_id: campData.place_id,
      name: campData.name
    };
  }
};

/**
 * Batch save multiple camps from API results
 */
export const batchSaveCamps = async (campsArray) => {
  console.log(`💾 Batch saving ${campsArray.length} camps to database...`);
  
  const results = await Promise.allSettled(
    campsArray.map(camp => saveCampToDatabase(camp))
  );
  
  const successful = results.filter(r => r.status === 'fulfilled').length;
  const failed = results.filter(r => r.status === 'rejected').length;
  
  console.log(`✅ Batch save complete: ${successful} saved, ${failed} failed`);
  
  return { successful, failed };
};

/**
 * Track when a camp is viewed
 * @param {string} placeId - Google Place ID
 */
export const trackCampView = async (placeId) => {
  try {
    await incrementEngagement(placeId, 'view_count');
  } catch (error) {
    console.error('Error tracking camp view:', error);
  }
};

/**
 * Track when a camp is saved
 * @param {string} placeId - Google Place ID
 */
export const trackCampSave = async (placeId) => {
  try {
    await incrementEngagement(placeId, 'save_count');
  } catch (error) {
    console.error('Error tracking camp save:', error);
  }
};

/**
 * Track when a camp is clicked
 * @param {string} placeId - Google Place ID
 */
export const trackCampClick = async (placeId) => {
  try {
    await incrementEngagement(placeId, 'click_count');
  } catch (error) {
    console.error('Error tracking camp click:', error);
  }
};

/**
 * Track user engagement with camps (legacy function, use specific track functions)
 * @deprecated Use trackCampView, trackCampSave, or trackCampClick instead
 */
export const trackCampEngagement = async (placeId, engagementType) => {
  try {
    const updateField = {
      view: 'view_count',
      save: 'save_count',
      click: 'click_count'
    }[engagementType];
    
    if (!updateField) return;
    
    await incrementEngagement(placeId, updateField);
    
  } catch (error) {
    console.error('❌ Failed to track engagement:', error);
  }
};

/**
 * Increment engagement metric using RPC with fallback
 * @param {string} placeId - Google Place ID
 * @param {string} field - Field to increment: 'view_count', 'save_count', or 'click_count'
 */
async function incrementEngagement(placeId, field) {
  try {
    const client = await getSupabaseClient();
    if (!client) {
      console.warn('Supabase client not available for engagement tracking');
      return;
    }
    
    // Try using RPC first (more efficient)
    const { error: rpcError } = await client.rpc('increment_camp_engagement', {
      p_place_id: placeId,
      p_field: field
    });

    if (rpcError) {
      console.warn('RPC increment failed, using direct update fallback:', rpcError);
      
      // Fallback: Direct increment
      const { data: current } = await client
        .from('camp_audit_data')
        .select(field)
        .eq('place_id', placeId)
        .single();
      
      if (current) {
        await client
          .from('camp_audit_data')
          .update({ [field]: (current[field] || 0) + 1 })
          .eq('place_id', placeId);
      }
    }

    // After incrementing engagement, update popularity score
    await updatePopularityScore(placeId);

  } catch (error) {
    console.error(`Error incrementing ${field}:`, error);
    throw error;
  }
}

/**
 * Calculate popularity score from Google Places data and engagement metrics
 * Updated to use Maximum tier fields
 * 
 * @param {Object} data - Combined Google and engagement data
 * @returns {number} Popularity score (0-100)
 */
export function calculatePopularityScore(data) {
  let score = 0;

  // Google Rating (0-30 points)
  if (data.rating && data.user_ratings_total) {
    const ratingScore = (data.rating / 5) * 20;  // 0-20 points
    const reviewCountScore = Math.min(data.user_ratings_total / 10, 10);  // 0-10 points
    score += ratingScore + reviewCountScore;
  }

  // User Engagement (0-40 points) - MOST IMPORTANT
  if (data.view_count || data.save_count || data.click_count) {
    score += Math.min(data.view_count * 0.1, 10);  // 0-10 points
    score += Math.min(data.save_count * 2, 15);    // 0-15 points (saves are valuable)
    score += Math.min(data.click_count * 1.5, 15); // 0-15 points
  }

  // Photo count (0-10 points)
  if (data.photo_count) {
    score += Math.min(data.photo_count, 10);
  }

  // Contact info completeness (0-10 points)
  if (data.has_website) score += 5;
  if (data.has_phone) score += 5;

  // Operating status (0-10 points)
  if (data.business_status === 'OPERATIONAL') {
    score += 10;
  }

  return Math.min(Math.round(score), 100);  // Cap at 100
}

/**
 * Update popularity score based on engagement metrics
 */
const updatePopularityScore = async (placeId) => {
  const client = await getSupabaseClient();
  if (!client) {
    console.warn('Supabase client not available for popularity score update');
    return;
  }
  
  const { data } = await client
    .from('camp_audit_data')
    .select('view_count, save_count, click_count, rating, user_ratings_total, photo_count, website, phone, business_status')
    .eq('place_id', placeId)
    .single();
  
  if (!data) return;
  
  const popularityScore = calculatePopularityScore({
    rating: data.rating,
    user_ratings_total: data.user_ratings_total,
    view_count: data.view_count,
    save_count: data.save_count,
    click_count: data.click_count,
    photo_count: data.photo_count,
    has_website: !!data.website,
    has_phone: !!data.phone,
    business_status: data.business_status
  });
  
  await supabase
    .from('camp_audit_data')
    .update({ popularity_score: popularityScore })
    .eq('place_id', placeId);
};

/**
 * Extract search keywords from camp data
 * Handles both transformed data and raw Google Places format
 * 
 * @param {Object} campData - Camp data object
 * @returns {Array<string>} Array of search keywords
 */
export function extractSearchKeywords(campData) {
  // If campData already has search_keywords from transform, use them
  if (campData.search_keywords && Array.isArray(campData.search_keywords)) {
    return campData.search_keywords;
  }

  // Otherwise, extract basic keywords
  const keywords = new Set();

  // Name
  if (campData.name) {
    campData.name.toLowerCase().split(/\s+/).forEach(word => {
      if (word.length > 2) keywords.add(word);
    });
  }

  // Location
  if (campData.formatted_address) {
    campData.formatted_address.toLowerCase().split(/[,\s]+/).forEach(word => {
      if (word.length > 2) keywords.add(word);
    });
  }

  // Categories
  const allCategories = campData.all_categories || campData.program_category?.all_categories || [];
  if (allCategories.length > 0) {
    allCategories.forEach(cat => {
      cat.toLowerCase().split(/\s+/).forEach(word => {
        if (word.length > 2) keywords.add(word);
      });
    });
  }

  // Keywords matched
  const keywordsMatched = campData.keywords_matched || campData.program_category?.keywords_matched || [];
  if (keywordsMatched.length > 0) {
    keywordsMatched.forEach(kw => keywords.add(kw.toLowerCase()));
  }

  // Google types
  if (campData.google_types) {
    campData.google_types.forEach(type => {
      type.split('_').forEach(word => keywords.add(word.toLowerCase()));
    });
  }

  return Array.from(keywords).filter(k => k.length > 2);
}

/**
 * Get ISO week number for a date
 * 
 * @param {Date} date - Date to get week number for
 * @returns {number} Week number (1-53)
 */
export function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

/**
 * Mark stale camps for refresh (run periodically)
 */
export const markStaleCampsForRefresh = async () => {
  const client = await getSupabaseClient();
  if (!client) {
    console.warn('Supabase client not available for marking stale camps');
    return;
  }
  
  const { error } = await client
    .from('camp_audit_data')
    .update({ needs_refresh: true })
    .gt('data_freshness_days', 30);
  
  if (error) {
    console.error('Failed to mark stale camps:', error);
  } else {
    console.log('✅ Marked stale camps for refresh');
  }
};
